import { SectionSummary } from '@/components/WorkspacePresentation';
/**
 * Action Center — the approval queue.
 *
 * This screen is deliberately closer to a code-review tool than to a chat: the
 * reviewer sees exactly what will change, at what risk, with what evidence, and
 * nothing happens until they decide.
 */

import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { actionApi } from '@/api/endpoints';
import { useAuth } from '@/app/auth';
import { Permission } from '@/app/permissions';
import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  RiskBadge,
  StatusBadge,
  UrlText,
} from '@/components/ui';
import {
  formatRelative,
} from '@/utils/format';
import type { ActionStatus, ActionSummary } from '@/types/api';

const STATUS_TABS: { label: string; value: ActionStatus | '' }[] = [
  { label: 'Awaiting approval', value: 'pending_approval' },
  { label: 'Approved', value: 'approved' },
  { label: 'In progress', value: 'executing' },
  { label: 'Completed', value: 'completed' },
  { label: 'Failed', value: 'failed' },
  { label: 'All', value: '' },
];

export function ActionQueuePage() {
  const { siteId } = useParams<{ siteId?: string }>();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const status = (params.get('status') as ActionStatus | null) ?? 'pending_approval';

  const query = useQuery({
    queryKey: ['actions', siteId ?? 'all', status],
    queryFn: () =>
      siteId
        ? actionApi.listForSite(siteId, { status: status || undefined, page_size: 100 })
        : actionApi.queue({ status: status || undefined, page_size: 100 }),
    // Executing and verifying actions move on their own; keep the view fresh.
    refetchInterval: status === 'executing' || status === 'approved' ? 5000 : false,
  });

  const bulkMutation = useMutation({
    mutationFn: (actionIds: string[]) =>
      actionApi.bulkApprove({ action_ids: actionIds, max_risk: 'LOW' }),
    onSuccess: async () => {
      setSelected(new Set());
      await queryClient.invalidateQueries({ queryKey: ['actions'] });
    },
  });

  function toggle(actionId: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(actionId)) next.delete(actionId);
      else next.add(actionId);
      return next;
    });
  }

  const items = query.data?.items ?? [];
  // Only LOW-risk actions can be bulk approved; anything else needs individual
  // review, and the API enforces the same rule.
  const lowRiskPending = items.filter(
    (action) => action.risk === 'LOW' && action.status === 'pending_approval',
  );

  return (
    <>
      <PageHeader
        title="Action center"
        description="Every change SEO RankFix proposes for your website. Nothing is applied until you approve it."
        actions={
          can(Permission.ACTION_APPROVE) && selected.size > 0 ? (
            <Button
              variant="primary"
              loading={bulkMutation.isPending}
              onClick={() => bulkMutation.mutate([...selected])}
            >
              Approve {selected.size} low-risk action{selected.size === 1 ? '' : 's'}
            </Button>
          ) : null
        }
      />

      {query.data && <SectionSummary items={[
        { label: 'Matching actions', value: query.data.total, detail: 'In the selected state' },
        { label: 'Low-risk pending in view', value: lowRiskPending.length, detail: 'Eligible for bulk review' },
        { label: 'Selected for review', value: selected.size, detail: 'Your approval is required' },
      ]} />}

      {bulkMutation.data && bulkMutation.data.skipped.length > 0 ? (
        <div className="mb-5">
          <Callout tone="warning" title="Some actions were not approved">
            <ul className="mt-1 space-y-0.5">
              {bulkMutation.data.skipped.slice(0, 5).map((entry) => (
                <li key={entry.action_id} className="text-xs">
                  {entry.reason}
                </li>
              ))}
            </ul>
          </Callout>
        </div>
      ) : null}

      {bulkMutation.isError ? (
        <div className="mb-5">
          <Callout tone="danger">
            {bulkMutation.error instanceof ApiError
              ? bulkMutation.error.message
              : 'Bulk approval failed.'}
          </Callout>
        </div>
      ) : null}

      <div className="workspace-state-tabs mb-5 flex flex-wrap gap-1 border-b border-border-subtle" role="group" aria-label="Action state">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value || 'all'}
            aria-pressed={status === tab.value}
            type="button"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('status', tab.value);
              setParams(next, { replace: true });
              setSelected(new Set());
            }}
            className={
              (params.get('status') ?? 'pending_approval') === tab.value ||
              (!params.get('status') && tab.value === 'pending_approval')
                ? 'border-b-2 border-brand px-3 py-2 text-sm font-medium text-brand'
                : 'border-b-2 border-transparent px-3 py-2 text-sm text-ink-muted hover:text-ink'
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {query.isLoading ? <LoadingState label="Loading actions" /> : null}
      {query.isError ? (
        <ErrorState message={(query.error as Error).message} onRetry={() => void query.refetch()} />
      ) : null}

      {query.data && items.length === 0 ? (
        <EmptyState
          title={
            status === 'pending_approval' ? 'Nothing awaiting approval' : 'No actions in this state'
          }
          description={
            status === 'pending_approval'
              ? 'Actions appear here when a recommendation is converted into a concrete change. Review a recommendation to create one.'
              : undefined
          }
        />
      ) : null}

      {items.length > 0 ? (
        <div className="space-y-3">
          {lowRiskPending.length > 1 && can(Permission.ACTION_APPROVE) ? (
            <label className="flex items-center gap-2 px-1 text-sm text-ink-muted">
              <input
                type="checkbox"
                checked={selected.size === lowRiskPending.length && selected.size > 0}
                onChange={(event) =>
                  setSelected(
                    event.target.checked ? new Set(lowRiskPending.map((a) => a.id)) : new Set(),
                  )
                }
                className="h-4 w-4 rounded border-border-strong text-brand focus:ring-brand"
              />
              Select all {lowRiskPending.length} low-risk actions
            </label>
          ) : null}

          {items.map((action) => (
            <ActionRow
              key={action.id}
              action={action}
              selectable={
                can(Permission.ACTION_APPROVE) &&
                action.risk === 'LOW' &&
                action.status === 'pending_approval'
              }
              selected={selected.has(action.id)}
              onToggle={() => toggle(action.id)}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

function ActionRow({
  action,
  selectable,
  selected,
  onToggle,
}: {
  action: ActionSummary;
  selectable: boolean;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <Card className="workspace-action-row transition-shadow hover:shadow-sm">
      <div className="flex items-start gap-3">
        {selectable ? (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            aria-label={`Select ${action.title}`}
            className="mt-1 h-4 w-4 rounded border-border-strong text-brand focus:ring-brand"
          />
        ) : (
          <span className="w-4" aria-hidden="true" />
        )}

        <div className="min-w-0 flex-1">
          <Link to={`/actions/${action.id}`} className="group block">
            <p className="text-sm font-medium text-ink group-hover:text-brand">{action.title}</p>
            {action.target_url ? <UrlText url={action.target_url} className="mt-0.5" /> : null}
          </Link>

          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <Badge tone="neutral">{action.action_type.replace(/_/g, ' ').toLowerCase()}</Badge>
            <RiskBadge risk={action.risk} />
            <StatusBadge status={action.status} />
            {action.is_ai_generated ? <Badge tone="brand">AI generated</Badge> : null}
            {action.executor_provider ? (
              <Badge tone="neutral">via {action.executor_provider}</Badge>
            ) : null}
            <span className="text-xs text-ink-subtle">{formatRelative(action.created_at)}</span>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <div className="text-right">
            <p className="tnum text-lg font-semibold text-ink">
              {Math.round(action.priority_score)}
            </p>
            <p className="text-[10px] text-ink-subtle">priority</p>
          </div>
          <Link
            to={`/actions/${action.id}`}
            className="rounded-md border border-border-strong px-3 py-1.5 text-xs font-medium text-ink hover:bg-surface-muted"
          >
            Review
          </Link>
        </div>
      </div>
    </Card>
  );
}
